import React, { useState, useEffect } from "react"
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom"
import {
  TrendingUp,
  Wallet,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  Sun,
  Moon,
  MessageSquare,
  Users,
  HelpCircle,
  Grid3x3,
  ArrowLeftRight,
  CreditCard,
  ListChecks,
  Check,
  Cpu,
  ShoppingCart,
} from "lucide-react"
import { Button } from "../../components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu"
import { authAPI, communityAPI, notificationAPI, getErrorMessage } from "../../utils/api"
import { useTheme } from "../../context/ThemeContext"
import { formatDate } from "../../utils/helpers"
import { toast } from "sonner"
import { User } from "lucide-react"
import NotificationsBell from "../../components/NotificationsBell"

const UserLayoutNew = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [user, setUser] = useState(null)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [communityBadge, setCommunityBadge] = useState(0)
  const [notificationBadge, setNotificationBadge] = useState(0)
  const { theme, updateTheme } = useTheme()

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await authAPI.getMe()
        setUser(response.data)
      } catch (error) {
        console.error("Error fetching user:", error)
        navigate("/login")
      }
    }
    fetchUser()
  }, [navigate])

  useEffect(() => {
    const fetchCommunityBadge = async () => {
      try {
        const response = await communityAPI.getPosts()
        const count = Array.isArray(response.data) ? response.data.length : 0
        setCommunityBadge(count > 99 ? "99+" : count)
      } catch (error) {
        console.error("Error fetching community badge:", error)
      }
    }
    fetchCommunityBadge()
  }, [])

  // Sidebar unread badge: refreshes alongside the bell.
  useEffect(() => {
    const fetchNotificationBadge = async () => {
      try {
        const response = await notificationAPI.getAll()
        const unread = Array.isArray(response.data)
          ? response.data.filter((item) => !item.is_read).length
          : 0
        setNotificationBadge(unread > 99 ? "99+" : unread)
      } catch (error) {
        console.error("Error fetching notifications:", error)
      }
    }
    fetchNotificationBadge()
    const id = setInterval(fetchNotificationBadge, 60000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    setSidebarOpen(false)
  }, [location.pathname])

  const toggleDarkMode = () => {
    updateTheme(theme === "dark" ? "light" : "dark")
  }

  const handleLogout = () => {
    localStorage.removeItem("token")
    localStorage.removeItem("refreshToken")
    navigate("/login")
    toast.success("Logged out successfully")
  }

  const navigation = [
    {
      name: "OVERVIEW",
      items: [
        { name: "Dashboard", href: "/user", icon: Grid3x3 },
        { name: "Market", href: "/user/markets", icon: TrendingUp },
        { name: "Wallets", href: "/user/wallets", icon: Wallet },
        { name: "News", href: "/user/news", icon: FileText },
        {
          name: "Exchange",
          href: "/user/trading-terminal",
          icon: ArrowLeftRight,
        },
        { name: "Orders", href: "/user/orders", icon: ListChecks },
        { name: "Buy Crypto", href: "/user/buy-crypto", icon: ShoppingCart },
        { name: "Bitcoin Mining", href: "/user/bitcoin-mining", icon: Cpu },
      ],
    },
    {
      name: "ACCOUNT",
      items: [
        {
          name: "Notification",
          href: "/user/notifications",
          icon: Bell,
          badge: notificationBadge,
        },
        {
          name: "Deposit & Withdraw",
          href: "/user/deposit-withdraw",
          icon: CreditCard,
        },
        {
          name: "Community",
          href: "/user/community",
          icon: Users,
          // badge: communityBadge,
        },
        { name: "Settings", href: "/user/settings", icon: Settings },
      ],
    },
  ]

  const bottomNav = [
    { name: "Support", href: "/user/support", icon: HelpCircle },
    { name: "Messages", href: "/user/messages", icon: MessageSquare },
  ]

  const isActive = (href) => {
    if (href === "/user") {
      return location.pathname === "/user"
    }
    return location.pathname.startsWith(href)
  }

  return (
    <div className="min-h-screen bg-[#F9F9F9] dark:bg-slate-950">
      {/* Mobile Top Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between bg-white px-4 py-3 shadow-sm border-b border-gray-200 dark:bg-slate-950 dark:border-slate-800 lg:hidden">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(true)}
            className="rounded-lg p-2 text-gray-700 hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-slate-800"
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" />
          </button>
            <Link to="/user">
              <img
                src={theme === "dark" ? "/images/logo.png" : "/images/logo-dark.png"}
                alt="Logo"
                className="h-8 cursor-pointer"
              />
            </Link>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleDarkMode}
            className="relative hover:bg-gray-100 dark:hover:bg-slate-800"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? (
              <Sun className="w-5 h-5 text-gray-300" />
            ) : (
              <Moon className="w-5 h-5 text-gray-600" />
            )}
          </Button>
          <NotificationsBell iconClassName="text-gray-600 dark:text-slate-300" />
        </div>
      </div>

      {/* Top Navigation Bar - Hidden for now to match image layout */}
      <nav
        className="fixed top-0 left-64 right-0 z-50 border-b lg:block hidden"
        style={{
          backgroundColor: "#FFFFFF",
          display: "none", // Hide top nav to match image
        }}
      >
        <div className="px-4">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {sidebarOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
              <h1 className="text-xl font-semibold ml-2 lg:ml-0 text-gray-900 dark:text-white">
                Aqua Blockchain
              </h1>
            </div>

            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleDarkMode}
                className="relative hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {theme === "dark" ? (
                  <Sun className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                ) : (
                  <Moon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                )}
              </Button>

              <NotificationsBell iconClassName="text-gray-600 dark:text-gray-400" />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center space-x-2"
                  >
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-medium ${
                        theme === "dark" ? "bg-blue-600" : "bg-[#062136]"
                      }`}
                    >
                      {user?.full_name?.charAt(0) || "A"}
                    </div>
                    <span className="hidden md:block text-sm text-gray-700 dark:text-gray-300">
                      {user?.full_name || "User"}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-2 border-b">
                    <p className="text-sm font-medium">{user?.full_name}</p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                  </div>
                  <DropdownMenuItem onClick={() => navigate("/user/profile")}>
                    <User className="w-4 h-4 mr-2" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/user/settings")}>
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="text-red-600"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </nav>

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 bottom-0 w-[270px] transform transition-transform duration-300 ease-in-out z-50 bg-white border-r border-gray-200 dark:bg-slate-950 dark:border-slate-800 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"
          } lg:translate-x-0`}
      >
        <div className="h-full flex flex-col overflow-y-auto p-8">
          {/* Logo Section */}
          <div className="mb-8 flex items-center justify-between gap-4">
            <Link to="/user" className="focus:outline-none">
              <img
                src={theme === "dark" ? "/images/logo.png" : "/images/logo-dark.png"}
                alt="Logo"
                className="h-8 cursor-pointer"
              />
            </Link>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleDarkMode}
                className="hidden lg:inline-flex hover:bg-gray-100 dark:hover:bg-slate-800"
                aria-label="Toggle theme"
              >
                {theme === "dark" ? (
                  <Sun className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                ) : (
                  <Moon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                )}
              </Button>
              <button
                onClick={() => setSidebarOpen(false)}
                className="rounded-lg p-2 text-gray-700 hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-slate-800 lg:hidden"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Navigation Sections */}
          <div className="flex-1">
            {navigation.map((section, sectionIdx) => (
              <div key={section.name} className={sectionIdx > 0 ? "mt-8" : ""}>
                <h3 className="text-xs font-semibold uppercase tracking-wider mb-3 px-3 pl-6 text-gray-500 dark:text-slate-400">
                  {section.name}
                </h3>
                <nav className="space-y-1">
                  {section.items.map((item) => {
                    const Icon = item.icon
                    const active = isActive(item.href)
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setSidebarOpen(false)}
                        className={`flex items-center justify-between px-3 py-2.5 pl-6 rounded-lg text-sm font-medium transition-all ${active
                            ? theme === "dark"
                              ? "bg-blue-600 text-white ring-1 ring-blue-500/40 shadow-sm"
                              : "bg-[#062136] text-white ring-1 ring-[#062136]/40 shadow-sm"
                            : "text-gray-700 hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-slate-800"
                          }`}
                      >
                        <div className="flex items-center">
                          <Icon
                            className={`w-5 h-5 mr-3 ${active ? "text-primary-foreground" : "text-gray-500 dark:text-slate-400"}`}
                          />
                          {item.name}
                        </div>
                        {item.badge && (
                          <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-xl text-red-500 bg-red-100 dark:bg-red-500/20 dark:text-red-300">
                            {item.badge}
                          </span>
                        )}
                      </Link>
                    )
                  })}
                </nav>
              </div>
            ))}
          </div>

          {/* Bottom Navigation - Stick to bottom */}
          <div className="mt-8 pt-6">
            <nav className="space-y-1">
              {bottomNav.map((item) => {
                const Icon = item.icon
                const active = isActive(item.href)
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center px-3 pl-6 py-2.5 text-sm font-medium rounded-xl transition-all ${active
                        ? theme === "dark"
                          ? "bg-blue-600 text-white ring-1 ring-blue-500/40 shadow-sm"
                          : "bg-[#062136] text-white ring-1 ring-[#062136]/40 shadow-sm"
                        : "text-gray-700 hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-slate-800"
                      }`}
                  >
                    <Icon
                      className={`w-5 h-5 mr-3 ${active ? "text-primary-foreground" : "text-gray-500 dark:text-slate-400"}`}
                    />
                    {item.name}
                  </Link>
                )
              })}
              <button
                onClick={handleLogout}
                className="flex items-center px-3 pl-6 w-full py-2.5 text-sm font-medium transition-colors bg-red-100/70 dark:bg-red-500/15 rounded-xl text-red-600 dark:text-red-300"
              >
                <LogOut className="w-5 h-5 mr-3 text-red-600" />
                Logout
              </button>
            </nav>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Main Content */}
      <main
        className="lg:pl-[270px] pt-16 lg:pt-0 min-h-screen overflow-x-hidden"
      >
        <Outlet context={{ user, setUser }} />
      </main>
    </div>
  )
}

export default UserLayoutNew
